import React, { useState, useEffect } from 'react';
import AdminLayout from './Common/AdminLayout';
import LoadingSkeleton from './Common/LoadingSkeleton';
import { axiosInstance, config } from "../../Axios/AxiosInstance";
import toast from "react-hot-toast";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaSave } from 'react-icons/fa';
import "../../asset/new/css/admin-modern.css";

const ContactUsEdit = () => {
  // Token
  let header = config;
  const [contactDetails, setContactDetails] = useState({
    email: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch contact details from API
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const response = await axiosInstance.get('contact-us');
        setContactDetails(response?.data?.data[0] || {
          email: '',
          phone: '',
          address: ''
        });
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
        toast.error("Failed to load contact details");
      }
    };

    fetchContactDetails();
    document.title = "Edit Contact Us | Admin";
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactDetails(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axiosInstance.post('contact-us', contactDetails, header);
      toast.success("Contact details updated successfully!");
      setSaving(false);
    } catch (error) {
      setError(error.message);
      toast.error("Failed to update contact details");
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title="Contact Us Settings"
      subtitle="Manage contact information displayed on your website"
    >
      <div className="admin-card">
        <div className="admin-card-body">
          {loading ? (
            <div style={{ maxWidth: '600px' }}>
              <LoadingSkeleton type="input" count={3} />
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
              {/* Email Field */}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  <FaEnvelope style={{ marginRight: '8px', color: 'var(--admin-primary)' }} />
                  Email Address
                </label>
                <input 
                  type="email" 
                  name="email" 
                  value={contactDetails?.email || ''} 
                  onChange={handleChange} 
                  className="admin-input"
                  placeholder="contact@example.com"
                  required 
                />
                <small className="admin-form-text">
                  Primary email for customer inquiries
                </small>
              </div>

              {/* Phone Field */}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  <FaPhone style={{ marginRight: '8px', color: 'var(--admin-success)' }} />
                  Phone Number
                </label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={contactDetails?.phone || ''} 
                  onChange={handleChange} 
                  className="admin-input"
                  placeholder="+1 (555) 123-4567"
                  required 
                />
                <small className="admin-form-text">
                  Contact phone number with country code
                </small>
              </div>

              {/* Address Field */}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  <FaMapMarkerAlt style={{ marginRight: '8px', color: 'var(--admin-danger)' }} />
                  Physical Address
                </label>
                <textarea 
                  name="address" 
                  value={contactDetails?.address || ''} 
                  onChange={handleChange} 
                  className="admin-textarea"
                  placeholder="Enter your business address"
                  rows="4"
                  required 
                />
                <small className="admin-form-text">
                  Complete business address including street, city, state, and postal code
                </small>
              </div>

              {/* Submit Button */}
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-xl)' }}>
                <button 
                  type="submit" 
                  className="admin-btn admin-btn-primary"
                  disabled={saving}
                >
                  <FaSave />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                
                {error && (
                  <div className="admin-alert admin-alert-danger" style={{ marginBottom: 0 }}>
                    {error}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Preview Card */}
      {!loading && (
        <div className="admin-card" style={{ marginTop: 'var(--spacing-xl)' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">Contact Information Preview</h3>
          </div>
          <div className="admin-card-body">
            <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--spacing-md)' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--gradient-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <FaEnvelope />
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--admin-text-primary)' }}>
                    Email
                  </p>
                  <p style={{ color: 'var(--admin-text-secondary)', margin: 0 }}>
                    {contactDetails?.email || 'Not set'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--spacing-md)' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--gradient-success)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <FaPhone />
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--admin-text-primary)' }}>
                    Phone
                  </p>
                  <p style={{ color: 'var(--admin-text-secondary)', margin: 0 }}>
                    {contactDetails?.phone || 'Not set'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--spacing-md)' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--gradient-danger)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--admin-text-primary)' }}>
                    Address
                  </p>
                  <p style={{ color: 'var(--admin-text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>
                    {contactDetails?.address || 'Not set'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ContactUsEdit;
